import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import CtaBanner from "@/components/feature/CtaBanner";
import Seo from "@/components/feature/Seo";
import JsonLd from "@/components/feature/JsonLd";
import SectionHeading from "@/components/base/SectionHeading";
import BlogImage from "@/components/feature/BlogImage";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { blogCategories, blogPosts, featuredPost } from "@/data/blogs";
import { formatDate } from "@/lib/format";

const PAGE_SIZE = 9;

export default function Blog() {
  const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const term = query.trim().toLowerCase();
  // While searching or filtering we show every matching article, including the
  // one currently featured at the top of the page.
  const isFiltering = active !== "All" || term.length > 0;

  const filtered = useMemo(() => {
    const source = isFiltering ? blogPosts : blogPosts.slice(1);
    return source.filter((post) => {
      const matchesCategory = active === "All" || post.category === active;
      const matchesQuery =
        !term ||
        post.title.toLowerCase().includes(term) ||
        post.excerpt.toLowerCase().includes(term) ||
        post.category.toLowerCase().includes(term) ||
        post.tags.some((tag) => tag.toLowerCase().includes(term));
      return matchesCategory && matchesQuery;
    });
  }, [active, term, isFiltering]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const resetPaging = () => setVisibleCount(PAGE_SIZE);

  return (
    <Layout>
      <Seo
        title="Health Information & Blog | Orthopaedic & Spine Health"
        description="Practical, trustworthy guidance on bone, joint and spine health from the team at Ortho Spine Care, Trichy — covering back pain, arthritis, sports injuries and recovery."
        path="/blog"
      />
      <JsonLd
        id="ld-blog-list"
        data={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Ortho Spine Care Health Information",
          url: "https://orthospine.care/blog",
          blogPost: blogPosts.slice(0, 10).map((post) => ({
            "@type": "BlogPosting",
            headline: post.title,
            url: `https://orthospine.care/blog/${post.slug}`,
            datePublished: post.publishedDate,
            image: post.featuredImage,
            author: { "@type": "Person", name: post.author },
          })),
        }}
      />

      <PageHero
        title="Health Information"
        subtitle="Practical, trustworthy guidance on bone, joint and spine health to help you make informed decisions about your care."
        crumbs={[{ label: "Home", to: "/" }, { label: "Blog" }]}
      />

      {featuredPost && !isFiltering ? (
        <section className="bg-background-50 pt-14 md:pt-20">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <Link
              to={`/blog/${featuredPost.slug}`}
              className="group grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-100/60 lg:grid-cols-12"
            >
              <div className="h-64 w-full overflow-hidden lg:col-span-6 lg:h-full">
                <BlogImage
                  src={featuredPost.featuredImage}
                  alt={featuredPost.imageAlt}
                  title={`${featuredPost.title} — Ortho Spine Care blog`}
                  className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col justify-center p-6 md:p-9 lg:col-span-6">
                <span className="inline-flex w-fit items-center rounded-full bg-accent-100 px-3 py-1 font-label text-xs font-semibold uppercase tracking-wider text-accent-800">
                  Featured · {featuredPost.category}
                </span>
                <h2 className="mt-4 font-heading text-2xl font-semibold leading-tight text-foreground-950 md:text-3xl">
                  {featuredPost.title}
                </h2>
                <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600 md:text-base">
                  {featuredPost.excerpt}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 font-label text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line" aria-hidden="true"></i>
                    {featuredPost.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line" aria-hidden="true"></i>
                    {formatDate(featuredPost.publishedDate)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    {featuredPost.readTime}
                  </span>
                </div>
                <span className="mt-6 inline-flex items-center gap-1.5 font-label text-sm font-semibold text-primary-700">
                  Read article
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
              </div>
            </Link>
          </div>
        </section>
      ) : null}

      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading
            eyebrow="Latest articles"
            title={isFiltering ? "Search results" : "Browse our health information"}
            description="Search by topic, or filter by category to find information relevant to your condition."
          />

          <div className="mt-8 flex flex-col items-center gap-5">
            <div className="relative w-full max-w-md">
              <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line" aria-hidden="true"></i>
              </span>
              <input
                type="search"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  resetPaging();
                }}
                placeholder="Search articles (e.g. back pain, knee, arthritis)"
                aria-label="Search articles"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-11 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>

            <div className="w-full max-w-full overflow-x-auto pb-1">
              <div className="mx-auto w-max">
                <Tabs
                  value={active}
                  onValueChange={(value) => {
                    setActive(value);
                    resetPaging();
                  }}
                >
                  <TabsList className="flex-nowrap">
                    <TabsTrigger value="All">All</TabsTrigger>
                    {blogCategories.map((category) => (
                      <TabsTrigger key={category} value={category} className="whitespace-nowrap">
                        {category}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
            </div>

            <p className="font-label text-xs text-foreground-500" aria-live="polite">
              {filtered.length} {filtered.length === 1 ? "article" : "articles"}
              {isFiltering ? " match your filters" : " available"}
            </p>
          </div>

          {visible.length ? (
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((post) => (
                <article
                  key={post.slug}
                  className="reveal flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100/60"
                >
                  <Link to={`/blog/${post.slug}`} className="block h-48 w-full overflow-hidden bg-background-100">
                    <BlogImage
                      src={post.featuredImage}
                      alt={post.imageAlt}
                      title={`${post.title} — Ortho Spine Care blog`}
                      className="h-full w-full object-cover object-top transition-transform duration-300 hover:scale-105"
                    />
                  </Link>
                  <div className="flex flex-1 flex-col p-6">
                    <span className="inline-flex w-fit items-center rounded-full bg-accent-100 px-2.5 py-1 font-label text-xs font-semibold text-accent-800">
                      {post.category}
                    </span>
                    <h3 className="mt-3 font-heading text-base font-semibold leading-snug text-foreground-950">
                      <Link to={`/blog/${post.slug}`} className="hover:text-primary-700">
                        {post.title}
                      </Link>
                    </h3>
                    <p className="mt-2 flex-1 font-body text-sm leading-relaxed text-foreground-600">{post.excerpt}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-3 font-label text-xs text-foreground-500">
                      <span className="flex items-center gap-1.5">
                        <i className="ri-calendar-line" aria-hidden="true"></i>
                        {formatDate(post.publishedDate)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-time-line" aria-hidden="true"></i>
                        {post.readTime}
                      </span>
                    </div>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="mt-4 inline-flex items-center gap-1.5 font-label text-sm font-semibold text-primary-700 hover:text-primary-800"
                    >
                      Read More
                      <i className="ri-arrow-right-line" aria-hidden="true"></i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100/60 p-10 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className="ri-search-line text-2xl" aria-hidden="true"></i>
              </span>
              <p className="mt-4 font-heading text-base font-semibold text-foreground-950">No articles found</p>
              <p className="mt-2 font-body text-sm text-foreground-600">
                Try a different search term or category.
              </p>
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setActive("All");
                  resetPaging();
                }}
                className="mt-5 cursor-pointer rounded-md border border-primary-300 px-5 py-2.5 font-label text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
              >
                Show all articles
              </button>
            </div>
          )}

          {hasMore ? (
            <div className="mt-10 text-center">
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-primary-300 px-6 py-3 font-label text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
              >
                Load more articles
                <i className="ri-arrow-down-line" aria-hidden="true"></i>
              </button>
            </div>
          ) : null}
        </div>
      </section>

      <CtaBanner
        title="Have a question about your health?"
        text="Our team is happy to help. Book a consultation with our orthopaedic and spine specialist."
      />
    </Layout>
  );
}